import {
  FieldSelect,
  FieldText,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import {
  NotificationPayloadT,
  Template,
  TemplatesUpdate,
  TemplateTypes,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { PureComponent } from 'react';
import { Field, InjectedFormProps, SubmissionError } from 'redux-form';

import { WithTemplatesInjectedProps } from 'hocs/templates/withTemplates';

import TemplateField from '../../../../../../shared/util/TemplateField';
import style from '../../templates.module.scss';

export type TemplatesAddModalFormValues = {
  name: string;
  content: string;
  subject: string;
  type: TemplateTypes;
};

export type TemplatesAddModalOwnProps = {
  isAddModalOpen: boolean;
  currentTemplate?: Template;
  onClose: (canceled?: boolean) => void;
  initialValues?: Template;
};

export type TemplatesAddModalStateProps = {};

export interface TemplatesAddModalDispatchProps {
  notificationAdd: (payload: NotificationPayloadT) => void;
}

export type TemplatesAddModalProps = WithTemplatesInjectedProps &
  TemplatesAddModalOwnProps &
  TemplatesAddModalStateProps &
  TemplatesAddModalDispatchProps;

type InjectedProps = InjectedFormProps<
  TemplatesAddModalFormValues,
  TemplatesAddModalProps
>;

type PropsT = TemplatesAddModalProps & InjectedProps;

export class TemplatesAddModalView extends PureComponent<PropsT> {
  @boundMethod
  handleFormSubmit(values: TemplatesAddModalFormValues) {
    const { currentTemplate, reset, templates, notificationAdd, onClose } =
      this.props;
    const { create, update } = templates;
    const reg = /[<>="']/g;

    if (reg.test(values.name)) {
      throw new SubmissionError({ name: 'Name: forbidden characters: <>="' });
    }

    if (!currentTemplate) {
      const payload = {
        name: values.name,
        subject: values.subject,
        content: values.content,
        type: values.type,
      };

      create(payload)
        .then((r) => {
          ampli.settingsTemplatesModalFormSubmitted({
            mode: 'add',
            templateType: values.type,
            templateName: values.name,
          });

          notificationAdd({
            title: `Template created`,
            text: `Template "${values.name}" created`,
            className: 'success',
            time: 5000,
            icon: 'i-done',
          }),
            reset();
          onClose();
        })
        .catch((e) => {
          notificationAdd({
            title: 'An error occurred',
            text: `An error occurred when trying to create the "${values.name}" template`,
            icon: 'i-exclamation',
            className: 'danger',
          });
          throw new SubmissionError({ name: 'Template already exist' });
        });
    } else {
      const payload: TemplatesUpdate = {
        updates: {
          content: values.content,
          subject: values.subject,
          name: currentTemplate.name,
        },
        deletes: [],
      };

      if (currentTemplate.name.trim() !== values.name.trim()) {
        payload.updates.name = values.name;
      }

      if (!values.subject) {
        payload.deletes.push('subject');
        delete payload.updates.subject;
      }
      update(currentTemplate.id, payload)
        .then((r) => {
          ampli.settingsTemplatesModalFormSubmitted({
            mode: 'edit',
            templateType: currentTemplate?.type ?? values.type,
            templateName: currentTemplate?.name,
          });

          notificationAdd({
            title: `Template updated`,
            text: `Template "${values.name}" updated`,
            className: 'success',
            time: 5000,
            icon: 'i-done',
          }),
            reset();
          onClose();
        })
        .catch((e) => {
          notificationAdd({
            title: 'An error occurred',
            text: `An error occurred when trying to update the "${values.name}" template`,
            icon: 'i-exclamation',
            className: 'danger',
          });
        });
    }
  }

  render() {
    const { handleSubmit, currentTemplate, isAddModalOpen, onClose } =
      this.props;
    const templateName =
      currentTemplate && currentTemplate.name ? currentTemplate.name : '';
    const modalTitle = currentTemplate
      ? `Edit template "${templateName}"`
      : 'Add new template';

    const templateTypes = [
      { id: 'quick_response', name: 'Quick response' },
      { id: 'email_auto_reply', name: 'Auto reply' },
    ];

    const helptext = `You can use these replacing variables eg. *{{agent_fullname}}* with the actual agent name.:

      {{organization_name}}
      {{requester_firstname}}
      {{requester_fullname}}
      {{requester_email}}
      {{agent_firstname}}
      {{agent_fullname}}
      {{conversation_subject}}
      {{csid}}

      _note_: csid is the conversation ID.`;

    return (
      <Modal isOpen={isAddModalOpen} onClose={() => onClose(true)}>
        <form onSubmit={handleSubmit}>
          <ModalHead title={modalTitle} onClose={() => onClose(true)} />
          <ModalBody className={style.templatesAddModalBody}>
            <FlexBox flexDirection="column">
              <Field
                label="Name"
                name="name"
                component={FieldText}
                type="text"
                required={true}
                placeholder="Name..."
                helpIconText="Template name should only be in lowercase and not contain any whitespace or special characters"
                helpIconTitle="Template name"
              />
              <Field
                label="Subject"
                name="subject"
                component={FieldText}
                type="text"
                required={true}
                placeholder="Subject..."
                helpIconTitle="Subject"
                helpIconText={`This field is used to fill out the subject line in emails when composing emails and for auto-replies. The subject line is not used if the template is used in chat or you are replying to an email. You can use these variables: ${helptext}`}
                className={style.templatesFormField}
              />
              {!currentTemplate && (
                <Field
                  label="Template type"
                  required={true}
                  name="type"
                  component={FieldSelect}
                  placeholder="Please select a template type"
                  items={templateTypes}
                  className={style.templatesFormField}
                />
              )}
              <div className={style.templatesFormField}>
                <Field
                  displayName="Template Content"
                  name="content"
                  component={TemplateField}
                  required={true}
                  placeholder="content..."
                  showHelpIcon={true}
                  helpIconTrigger={['click']}
                  helpIconTitle="Template Content"
                  helpIconText={`The content is written in HTML and supports [Liquid](https://shopify.github.io/liquid/) too. \n\n${helptext}`}
                />
              </div>
            </FlexBox>
          </ModalBody>
          <ModalFoot
            cancelOnClick={() => onClose(true)}
            confirmOnClick={handleSubmit(this.handleFormSubmit)}
            cancelName="Cancel"
            confirmName="Save"
            confirmProps={{ type: 'submit' }}
          />
        </form>
      </Modal>
    );
  }
}
